import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "applicant" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register(form);
      toast.success("Account created — welcome to Cadastre360");
      nav("/app");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 lg:p-12">
      <div className="w-full max-w-md">
        <div className="mb-10 flex items-center">
          <BrandMark size={152} />
        </div>
        <div className="cad-panel p-7">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">Applicant Registration</div>
          <div className="font-display text-2xl font-bold mt-2 text-[#F6F8FA]">Create your account</div>
          <form onSubmit={submit} className="space-y-4 mt-5" data-testid="register-form">
            {[
              { k: "name", label: "Full name", type: "text" },
              { k: "email", label: "Email", type: "email" },
              { k: "password", label: "Password", type: "password" },
            ].map((f) => (
              <div key={f.k}>
                <label className="block text-[11px] font-mono-cad uppercase tracking-[0.18em] text-[#B3C2CC] mb-1">{f.label}</label>
                <input
                  data-testid={`register-${f.k}`}
                  required type={f.type} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)}
                  className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2.5 text-[14px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] focus:border-transparent outline-none"
                />
              </div>
            ))}
            <div>
              <label className="block text-[11px] font-mono-cad uppercase tracking-[0.18em] text-[#B3C2CC] mb-1">Role</label>
              <select
                data-testid="register-role"
                value={form.role} onChange={(e) => set("role", e.target.value)}
                className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2.5 text-[14px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] outline-none"
              >
                <option value="applicant">Applicant / Public</option>
                <option value="surveyor">Registered Surveyor</option>
                <option value="rights_holder">Rights Holder / Company</option>
                <option value="investor">Investor</option>
              </select>
            </div>
            {err && <div className="text-[13px] text-[#E67E7E]" data-testid="register-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="register-submit"
                    className="cad-btn-primary w-full py-3 rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />}
              {busy ? "Creating account" : "Create account"}
            </button>
            <div className="text-[12px] text-[#B3C2CC]">
              Have an account? <Link to="/login" className="text-[#C99A2E] hover:underline">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
